import { Chip, IconVariants } from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useMemo } from "react";

import { LinkWrapper } from "@/components/customComponents";
import { cn } from "@/utils";
import { tagNameToUrlSlug } from "@/utils/tagUrlSlug";

const TAG_LINK_CHIP_BASE_CLASSNAME =
  "inline-flex items-center h-[32px] px-3.5 rounded-full border-0 text-sm cursor-pointer w-fit no-underline hover:opacity-90";

const TAG_ADDED_LINK_CHIP_CLASSNAME = cn(TAG_LINK_CHIP_BASE_CLASSNAME, "bg-success dark:!bg-green-5 text-white");

export type TagSuggestionItemCommonProps = {
  isMenuOpen: boolean;
  getTranslatedTag: (tagName: string) => string;
  getTagAriaLabel: (tagName: string) => string;
};

export type TagSuggestionItemProps = TagSuggestionItemCommonProps & {
  tag: string;
  mode: "existing" | "added";
  isMarkedForRemoval?: boolean;
  onChipClick?: (tagName: string) => void;
  "data-testid"?: string;
};

function TagSuggestionItem({
  tag,
  mode,
  isMenuOpen,
  isMarkedForRemoval = false,
  onChipClick,
  getTranslatedTag,
  getTagAriaLabel,
  "data-testid": testId = "TagSuggestionItem",
}: TagSuggestionItemProps) {
  const tagLabel = getTranslatedTag(tag);
  const ariaLabel = getTagAriaLabel(tag);
  const isExistingTag = mode === "existing";
  const elementTestId = `${testId}-${tag}`;

  const editModeChipClassName = useMemo(
    () => cn(isExistingTag && isMarkedForRemoval && "!bg-error !text-white", !isExistingTag && "bg-success dark:!bg-green-5 text-white"),
    [isExistingTag, isMarkedForRemoval],
  );

  const defaultViewLinkClassName = useMemo(() => {
    if (!isExistingTag) {
      return TAG_ADDED_LINK_CHIP_CLASSNAME;
    }

    return cn(TAG_LINK_CHIP_BASE_CLASSNAME, isMarkedForRemoval ? "bg-error text-white" : "bg-gray-4 dark:bg-gray-6 text-gray-12 dark:text-white");
  }, [isExistingTag, isMarkedForRemoval]);

  const handleChipClick = useCallback(() => {
    onChipClick?.(tag);
  }, [onChipClick, tag]);

  if (isMenuOpen) {
    return (
      <Chip
        data-testid={elementTestId}
        className={editModeChipClassName}
        onClick={handleChipClick}
        iconEnd={isExistingTag ? IconVariants.CLOSE : IconVariants.CHECK}
        aria-label={ariaLabel}
      >
        {tagLabel}
      </Chip>
    );
  }

  return (
    <LinkWrapper data-testid={elementTestId} to={`/chatbot_tag/${tagNameToUrlSlug(tag)}`} className={defaultViewLinkClassName} aria-label={ariaLabel}>
      {tagLabel}
    </LinkWrapper>
  );
}

export default React.memo(TagSuggestionItem);
